import { Alert, Container, Modal, Typography } from "@mui/material";
import { Box, styled } from "@mui/system";
import React, { useContext } from "react";
import { trackEvent } from "src/Amplitude";
import AuthContext from "src/providers/AuthProvider";
import { AcceptButton } from "src/components/disclosures/AcceptButton";
import Layout from "src/components/core/Layout";
import GuestBanner from "src/components/dashboard/GuestBanner";
import GenericModal from "src/components/dca/GenericModal";
import LegalDialog from "src/components/legal/LegalDialog";
import WelcomeModal from "src/components/onboarding/WelcomeModal";
import TrEducationModal from "src/components/transfers/TrEducationModal";
import Announcement from "src/components/portfolio/Announcement";
import Balance from "src/components/portfolio/Balance";
import News from "src/components/portfolio/News";
import RecentTransactions from "src/components/portfolio/RecentTransactions";
import Referrals from "src/components/portfolio/Referrals";
import Summary from "src/components/portfolio/Summary";
import YourCrypto from "src/components/portfolio/YourCrypto";
import useDeviceSizes from "src/components/portfolio/hooks/useDeviceSizes";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";
import useSumsubStatus from "src/components/portfolio/hooks/useSumsubStatus";
import {
  AnnouncementWrapper,
  DBalancePanel,
  DGridWrapper,
  DLeftColumn,
  DReferralPanel,
  DRightColumn,
  DSummaryPanel,
  GuestBannerWrapper,
  HeaderHello,
  HeaderWrapper,
  NewsPanel,
  TBalancePanel,
  TGridWrapper,
  TransactionsPanel,
  TReferralPanel,
  TSummaryPanel,
  YourCryptoPanel,
} from "src/components/portfolio/styles/portifolio.styles";
import { useUserSlice, setUserStateDisclosures, type StateDisclosureData } from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import usePermission from "src/utils/hooks/usePermission";
import { usStateCode } from "src/utils/usStates";
import { Info } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { DisclosureContentModal } from "src/components/disclosures/DisclosureContentModal";
import useStateDisclosure from "src/utils/hooks/useStateDisclosure";
import { useAcceptStateDisclosureMutation } from "src/store/features/api/generated";

const STATE_DISCLOSURE_LOGOUT_WARNING =
  "Closing this will log you out, please accept the disclosure to continue using the Portal.";

const LogoutWarningModalContainer = styled(Box)(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  outline: "none",
  padding: "40px",
  alignItems: "center",
  gap: "24px",
  borderRadius: "28px",
  boxShadow: "0 4px 20px 0 rgba(173, 192, 203, 0.30)",
  width: "95%",
  maxWidth: "518px",
  "@media (min-width: 768px)": {
    width: "90%",
  },
  "@media (min-width: 1024px)": {
    width: "518px",
  },
}));

const LogoutWarningTitle = styled(Typography)(() => ({
  fontFamily: "Roobert",
  color: "#002035",
  textAlign: "center",
  width: "100%",
  fontSize: "24px",
  fontWeight: 500,
  lineHeight: "28px",
  letterSpacing: "-0.48px",
}));

const LogoutWarningMessageBox = styled(Box)({
  display: "flex",
  padding: "12px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "10px",
  background: "#F8F9F9",
  color: "#002035",
});

const LogoutWarningMessage = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  color: "#002035",
});

const LogoutWarningActions = styled(Box)({
  display: "flex",
  gap: "12px",
  alignSelf: "stretch",
  width: "100%",
});

const Portfolio = () => {
  const { logout } = useContext(AuthContext);

  // Create modal config from sumsub status
  const { sumsubModalConfig, showPendingAlert } = useSumsubStatus();

  const [useDefaultTheme, toggle] = React.useReducer((theme: any) => !theme, true);
  const [showLogoutWarning, setShowLogoutWarning] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  // get device sizes
  const { isDesktop, isTablet, isMobile } = useDeviceSizes();
  const isUkUser = useIsUkUserCountry();

  // is user guest?
  const { userPermission } = usePermission();
  // get users name and if user is unvirified
  const dispatch = useAppDispatch();
  const {
    details: { name },
    isUnverified,
    address,
    stateDisclosures,
  } = useUserSlice();

  // RTK Query hook for accepting state disclosure
  const [acceptStateDisclosure] = useAcceptStateDisclosureMutation();

  // State-specific disclosure modal hook
  const {
    selectedDisclosure,
    modalProps,
    handleClose,
    disclosureVersion,
    region,
  } = useStateDisclosure(async () => {
    // Record the backend-resolved region code ("CA"), same as native
    const state = region ?? usStateCode(address?.state);

    if (state) {
      try {
        await acceptStateDisclosure({
          acceptStateDisclosureDto: {
            state,
            isAccepted: true,
            ...(disclosureVersion != null && {
              disclosureVersion,
            }),
          },
        }).unwrap();
        // Optimistically append so Redux matches backend's append-only list
        const newEntry: StateDisclosureData = {
          state,
          accepted: true,
          timestamp: Date.now(),
          ...(disclosureVersion != null && {
            disclosureVersion,
          }),
        };
        dispatch(setUserStateDisclosures([...(stateDisclosures ?? []), newEntry]));
      } catch (error) {
        console.error("Error accepting state disclosure:", error);
      }
    }
  });

  // When user tries to close state disclosure (backdrop/escape), show warning; on accept just close
  const stateDisclosureModalProps = React.useMemo(
    () => ({
      ...modalProps,
      onClose: (_event?: any, reason?: string) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          setShowLogoutWarning(true);
        } else if (reason === "accept") {
          handleClose();
        }
      },
    }),
    [modalProps, handleClose],
  );

  const handleLogoutWarningGoBack = () => {
    if (isLoggingOut) return;
    setShowLogoutWarning(false);
  };

  const handleLogoutWarningConfirm = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      setShowLogoutWarning(false);
      handleClose();
    } finally {
      setIsLoggingOut(false);
    }
  };

  React.useEffect(() => {
    if (userPermission) trackEvent("Visit_Page", { page: "Portfolio" });
  }, [userPermission]);

  // get todays date
  const todaysDate = dateFormatter(new Date());

  return (
    <Layout toggleTheme={toggle} useDefaultTheme={useDefaultTheme}>
      <Container disableGutters={isMobile} maxWidth="xl">
        {/* SUMSUB PENDING ACCOUNT ALERT */}
        {showPendingAlert && (
          <Alert icon={<Info fontSize="inherit" />} severity="info" sx={{ mb: 2 }}>
            <Typography fontSize={14}>
              We received your Entity Onboarding application. Please allow between 24-72 hours for
              processing. If you have any questions, please contact support.
            </Typography>
            <Link to="/entity-onboarding">Click here for more information</Link>
          </Alert>
        )}
        {/* HEADER */}
        <HeaderWrapper>
          <div>
            <HeaderHello>Hello </HeaderHello>
            {name}!
          </div>
          <div>{todaysDate}</div>
        </HeaderWrapper>

        {/* ANNOUNCEMENT */}
        <AnnouncementWrapper>
          <Announcement />
        </AnnouncementWrapper>

        {/* GUEST BANNER */}
        {isUnverified && (
          // This is a guest banner that shows up when the user is unverified and not pending verification
          <GuestBannerWrapper>
            <GuestBanner variant={0} />
          </GuestBannerWrapper>
        )}

        {/* DOCUMENTS */}
        <WelcomeModal />
        <TrEducationModal />
        <LegalDialog />

        {isDesktop && (
          <DGridWrapper>
            <DLeftColumn>
              {/* BALANCE COMPONENT */}
              <DBalancePanel>
                <Balance />
              </DBalancePanel>

              {/* WATCHLIST COMPONENT*/}
              <YourCryptoPanel>
                <YourCrypto />
              </YourCryptoPanel>

              {/* NEWS COMPONENT */}
              <NewsPanel>
                <News />
              </NewsPanel>
            </DLeftColumn>

            <DRightColumn>
              {/* REFERRAL COMPONENT */}
              {!isUkUser && (
                <DReferralPanel>
                  <Referrals />
                </DReferralPanel>
              )}
              {/* SUMMARY COMPONENT */}
              <DSummaryPanel>
                <Summary />
              </DSummaryPanel>
              {/* TRANSACTION COMPONENT */}
              <TransactionsPanel>
                <RecentTransactions />
              </TransactionsPanel>
            </DRightColumn>
          </DGridWrapper>
        )}
        {(isTablet || isMobile) && (
          <TGridWrapper>
            {/* BALANCE COMPONENT */}
            <TBalancePanel>
              <Balance />
            </TBalancePanel>
            {/* REFERRAL COMPONENT */}
            {!isUkUser && (
              <TReferralPanel>
                <Referrals />
              </TReferralPanel>
            )}
            {/* SUMMARY COMPONENT */}
            <TSummaryPanel>
              <Summary />
            </TSummaryPanel>
            {/* WATCHLIST COMPONENT */}
            <YourCryptoPanel>
              <YourCrypto />
            </YourCryptoPanel>
            {/* TRANSACTION COMPONENT */}
            <TransactionsPanel>
              <RecentTransactions />
            </TransactionsPanel>
            {/* NEWS COMPONENT */}
            <NewsPanel>
              <News />
            </NewsPanel>
          </TGridWrapper>
        )}
        {/* SUMSUB MODAL */}
        {sumsubModalConfig && <GenericModal {...sumsubModalConfig} />}

        {/* STATE-SPECIFIC DISCLOSURE MODAL */}
        {selectedDisclosure && <DisclosureContentModal {...stateDisclosureModalProps} />}

        {/* WARNING WHEN USER TRIES TO CLOSE STATE DISCLOSURE WITHOUT ACCEPTING */}
        <Modal open={showLogoutWarning} onClose={handleLogoutWarningGoBack}>
          <LogoutWarningModalContainer>
            <LogoutWarningTitle>Log out?</LogoutWarningTitle>
            <LogoutWarningMessageBox>
              <LogoutWarningMessage>{STATE_DISCLOSURE_LOGOUT_WARNING}</LogoutWarningMessage>
            </LogoutWarningMessageBox>
            <LogoutWarningActions>
              <AcceptButton
                variant="secondary"
                onClick={handleLogoutWarningGoBack}
                disabled={isLoggingOut}
                fullWidth>
                Go back
              </AcceptButton>
              <AcceptButton
                variant="primary"
                onClick={handleLogoutWarningConfirm}
                disabled={isLoggingOut}
                loading={isLoggingOut}
                fullWidth>
                Log out
              </AcceptButton>
            </LogoutWarningActions>
          </LogoutWarningModalContainer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Portfolio;
